import { Dispatch, SetStateAction, useMemo } from "react";
import {
  Box,
  Divider,
  FormControlLabel,
  FormGroup,
  Stack,
  Switch,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSelector } from "../../../../app/helpers";
import { RootState } from "../../../../app/rootReducer";
import { MetaData } from "../../../Login/typings/login.types";
import { SettingsAccordion } from "./CompanyCard-Setting-Accordion.partial";
import {
  BookingTypeEnableMetaKey,
  BookingTypeEnablingRowConfig,
  filterVisibleBookingTypeEnablingRows,
  getBookingTypeEnabled
} from "./company-card-booking-type-enabling.config";

type BookingTypeEnablingAccordionProps = {
  newValues: MetaData;
  setNewValues: Dispatch<SetStateAction<MetaData>>;
};

type BookingTypeEnableSwitchProps = {
  labelKey: string;
  checked: boolean;
  testId: string;
  onCheckedChange: (checked: boolean) => void;
};

const bookingTypeEnableLabelSx = { my: 0, mx: 0, ml: 0, width: "100%" } as const;

function BookingTypeEnableSwitch({
  labelKey,
  checked,
  testId,
  onCheckedChange
}: Readonly<BookingTypeEnableSwitchProps>) {
  const { t } = useTranslation();

  return (
    <FormControlLabel
      sx={bookingTypeEnableLabelSx}
      control={
        <Switch
          checked={checked}
          data-testid={testId}
          onChange={(_e, c) => onCheckedChange(c)}
          slotProps={{ input: { "aria-label": labelKey } }}
        />
      }
      label={t(labelKey)}
    />
  );
}

export function BookingTypeEnablingAccordion({
  newValues,
  setNewValues
}: Readonly<BookingTypeEnablingAccordionProps>) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const {
    userInformation: { placeTypes, zoneTypes, visitorManagementEnabled }
  } = useSelector((state: RootState) => state.login);

  const visibleRows = useMemo(
    () =>
      filterVisibleBookingTypeEnablingRows({
        placeTypeIds: new Set(placeTypes.map(pt => pt.id)),
        zoneTypeIds: new Set(zoneTypes.map(zt => zt.id)),
        visitorManagementEnabled
      }),
    [visitorManagementEnabled, placeTypes, zoneTypes]
  );

  const workplaceRow = visibleRows.find(row => row.id === "workplace");
  const otherTypeRows = visibleRows.filter(row => row.id !== "workplace");

  const workplaceBookingEnabled = getBookingTypeEnabled(newValues, "workplaceBookingEnabled");
  const nestedIndent = isMobile ? 2 : 3;

  const setBookingTypeEnabled = (metaKey: BookingTypeEnableMetaKey, enabled: boolean) => {
    setNewValues(prevState => {
      const next = { ...prevState, [metaKey]: enabled };

      if (metaKey === "workplaceBookingEnabled" && !enabled) {
        next.teamBookingAllowed = false;
      }

      return next;
    });
  };

  const renderEnableRow = (row: BookingTypeEnablingRowConfig) => {
    const enabled = getBookingTypeEnabled(newValues, row.metaKey);

    return (
      <BookingTypeEnableSwitch
        key={row.id}
        labelKey={row.labelKey}
        checked={enabled}
        testId={`enableBookingType-${row.id}-switch`}
        onCheckedChange={c => setBookingTypeEnabled(row.metaKey, c)}
      />
    );
  };

  return (
    <SettingsAccordion title={t("_companyCard_bookingTypeEnabling")}>
      <FormGroup sx={[isMobile ? { gap: 1 } : {}]}>
        <Stack spacing={0.5}>
          {workplaceRow && (
            <Box>
              {renderEnableRow(workplaceRow)}

              {workplaceBookingEnabled && (
                <Box
                  sx={{
                    pl: theme.spacing(nestedIndent),
                    pt: 0.5,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1
                  }}
                >
                  <FormControlLabel
                    sx={bookingTypeEnableLabelSx}
                    control={
                      <Switch
                        checked={newValues.teamBookingAllowed}
                        data-testid="enableTeamBookingAllowed-switch"
                        onChange={(_e, c) => {
                          setNewValues(prevState => ({
                            ...prevState,
                            teamBookingAllowed: c
                          }));
                        }}
                        name="teamBookingAllowed"
                        slotProps={{ input: { "aria-label": "teamBookingAllowed" } }}
                      />
                    }
                    label={t("enableTeamBookingAllowed")}
                  />
                </Box>
              )}

              {workplaceBookingEnabled && otherTypeRows.length > 0 && (
                <Divider sx={{ opacity: 0.6, mt: 0.5 }} />
              )}
            </Box>
          )}

          {otherTypeRows.length > 0 && (
            <Stack spacing={0.5}>{otherTypeRows.map(row => renderEnableRow(row))}</Stack>
          )}
        </Stack>
      </FormGroup>
    </SettingsAccordion>
  );
}
